import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { PetInsuranceData, PetInsuranceView } from "../../../models";
import { ApiHttpService } from "../../../services/api-http.service";
import { DataStorageService } from "../../../services/data-storage.service";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: 'cpf-pet-card',
  templateUrl: './pet-card.component.html',
  styleUrls: ['./pet-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PetCardComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() public showSubmitButton: boolean = true;

  @Input() set petInfo(value: PetInsuranceData) {
    this.pet = {
      full_contract_number: value.full_contract_number,
      base_number: value.base_number,
      name: value.pet_name,
      annualFranchiseOverall: value.annual_franchise,
      annualFranchiseRemaining: value.usedFranchise < 0 ? 0 : value.usedFranchise,
      insuredSumOverall: value.overall_sum_insured,
      insuredSumRemaining: value.paidAmount < 0 ? 0 : value.paidAmount,
      contractStatus: value.contractStatus,
      species: value.pet_species
    };

    setTimeout(() => {
      this.updateProgress(this.pet!);
    }, 100);

    this.getPetImageUrl();
  }

  @Output() actionButtonClickEmitter = new EventEmitter<void>();

  @ViewChild('diagramOuterPath', { static: false }) diagramOuterPath!: ElementRef<SVGPathElement>;
  @ViewChild('diagramInnerPath', { static: false }) diagramInnerPath!: ElementRef<SVGPathElement>;

  pet?: PetInsuranceView;

  private outerProgress = 0;
  private innerProgress = 0;

  constructor(private apiHttpService: ApiHttpService, private dataStorageService: DataStorageService) {
    this.dataStorageService.petPictureUpdated$.pipe(takeUntil(this.destroy$)).subscribe(baseNumber => {
      if (this.pet?.base_number === baseNumber) {
        this.getPetImageUrl();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getPetImageUrl() {
    if (!this.pet) {
      return
    }

    this.pet.image = (await this.apiHttpService.getUrlForPetPicture(this.pet.base_number)).Location || (this.pet.species === 'cat' ? '/assets/cat_icon_placeholder.png' : '/assets/dog_icon_placeholder.png');
  }

  updateProgress(value: PetInsuranceView) {
    let innerProgress = value.annualFranchiseRemaining / value.annualFranchiseOverall * 100;
    if (Number.isNaN(innerProgress)) {
      innerProgress = 0;
    }

    if (innerProgress < 0) {
      innerProgress = 0;
    } else if (innerProgress > 100) {
      innerProgress = 100;
    }

    let outerProgress = value.insuredSumRemaining / value.insuredSumOverall * 100;
    if (Number.isNaN(outerProgress)) {
      outerProgress = 0;
    }

    if (outerProgress < 0) {
      outerProgress = 0;
    } else if (outerProgress > 100) {
      outerProgress = 100;
    }

    this.changeOuterProgress(outerProgress);
    this.changeInnerProgress(innerProgress);
  }

  actionButtonClick() {
    this.actionButtonClickEmitter.emit();
  }

  changeOuterProgress(percentage: number) {
    this.updateOuterProgress(percentage);
    this.outerProgress = percentage;
  }

  changeInnerProgress(percentage: number) {
    this.updateInnerProgress(percentage);
    this.innerProgress = percentage;
  }

  private updateOuterProgress(percentage: number) {
    if (percentage < 0) {
      percentage = 0;
    }

    // Don't allow 100% because it looks same as 0%
    if (percentage >= 99.9) {
      percentage = 99.9;
    }

    const radius = 56;
    const angle = (percentage / 100) * 360;
    const radians = (angle - 90) * (Math.PI / 180); // Adjust for starting at the top
    const x = 60 + radius * Math.cos(radians);
    const y = 60 + radius * Math.sin(radians);
    const largeArcFlag = (percentage < 50) ? 0 : 1;
    this.diagramOuterPath.nativeElement.setAttribute('d', `M 60 4 A 56 56 0 ${largeArcFlag} 1 ${x} ${y}`);
  }

  private updateInnerProgress(percentage: number) {
    if (percentage < 0) {
      percentage = 0;
    }

    // Don't allow 100% because it looks same as 0%
    if (percentage >= 99.9) {
      percentage = 99.9;
    }

    const radius = 47;
    const angle = (percentage / 100) * 360;
    const radians = (angle - 90) * (Math.PI / 180); // Adjust for starting at the top
    const x = 51 + radius * Math.cos(radians);
    const y = 51 + radius * Math.sin(radians);
    const largeArcFlag = (percentage < 50) ? 0 : 1;
    this.diagramInnerPath.nativeElement.setAttribute('d', `M 51 4 A 47 47 0 ${largeArcFlag} 1 ${x} ${y}`);
  }
}
